import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import styles from './FlaskWidget.module.css';

/**
 * FlaskWidget — composition root for the interactive flask experience.
 *
 * ALW-28 scope: this is the dependency baseline + lazy-canvas spike. It mounts
 * an R3F canvas (a rotating placeholder mesh) only once the widget scrolls into
 * view, so the heavy three.js / react-three-fiber bundle is split into a
 * separate chunk and never blocks first paint. Later Tasks replace the spike
 * with the real flask scene, add the terminal pane, and build the two-pane
 * responsive layout.
 */

// React.lazy keeps the 3D code (and its three.js dependency tree) out of the
// initial JS chunk. Webpack emits it as a separate, on-demand bundle.
const CanvasSpike = lazy(() => import('./scene/CanvasSpike'));

export default function FlaskWidget() {
  const hostRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = hostRef.current;
    if (!node) return undefined;

    // No IntersectionObserver (older browsers / jsdom): fall back to mounting
    // immediately so the widget still renders.
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={hostRef} className={styles.viewport}>
      {inView && (
        <Suspense fallback={<span className={styles.loading}>initializing…</span>}>
          <CanvasSpike />
        </Suspense>
      )}
    </div>
  );
}
